import React, { useState, useRef } from 'react';
import { Auth } from 'aws-amplify';
import { FaTimes } from 'react-icons/fa';
import TextEdit from '../../../molecules/TextEdit';
import { PersonSearchResult, ApiListResponse } from '../types/api-responses';
import appConfig from '../../../../config/app-config';

const { address: PERSON_SERVICE_URL, key: API_KEY_PERSON } = appConfig.apis.personApiEnv;
const SEARCH_DEBOUNCE_MS = 500;
const MIN_SEARCH_LENGTH = 3;

interface PersonSearchModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSelect: (person: PersonSearchResult) => void;
}

const PersonSearchModal: React.FC<PersonSearchModalProps> = ({ isOpen, onClose, onSelect }) => {
    const [searchValue, setSearchValue] = useState('');
    const [searchResults, setSearchResults] = useState<Array<{ value: string, label: string, personData: PersonSearchResult }>>([]);
    const [isSearching, setIsSearching] = useState(false);
    const debounceTimer = useRef<NodeJS.Timeout | null>(null);
    const abortController = useRef<AbortController | null>(null);

    const handleSearch = (value: string) => {
        setSearchValue(value);

        if (debounceTimer.current) {
            clearTimeout(debounceTimer.current);
        }

        if (abortController.current) {
            abortController.current.abort();
        }

        if (value.length >= MIN_SEARCH_LENGTH) {
            setIsSearching(true);
            debounceTimer.current = setTimeout(async () => {
                abortController.current = new AbortController();

                try {
                    const token = await Auth.currentSession()
                        .then((user) => user.getIdToken().getJwtToken());

                    const response = await fetch(`${PERSON_SERVICE_URL}/person/ALL/${value}`, {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'x-api-key': API_KEY_PERSON || ''
                        },
                        signal: abortController.current.signal
                    });

                    if (response.ok) {
                        const data: ApiListResponse<PersonSearchResult> = await response.json();
                        const personItems = data.results.map((item) => {
                            const cnpjDoc = item.documents?.find((doc) => doc.type === 'CNPJ');
                            const cpfDoc = item.documents?.find((doc) => doc.type === 'CPF');
                            const document = cnpjDoc?.number || cpfDoc?.number || '';
                            return {
                                value: item.uuid,
                                label: `${document} - ${item.name}`,
                                personData: item
                            };
                        });
                        setSearchResults(personItems);
                    }
                } catch (error: unknown) {
                    if (error instanceof Error && error.name === 'AbortError') return;
                } finally {
                    setIsSearching(false);
                }
            }, SEARCH_DEBOUNCE_MS);
        } else {
            setSearchResults([]);
            setIsSearching(false);
        }
    };

    const handleSelect = (result: { value: string; label: string; personData: PersonSearchResult }) => {
        onSelect(result.personData);
        setSearchValue('');
        setSearchResults([]);
        onClose();
    };

    const handleClose = () => {
        onClose();
        setSearchValue('');
        setSearchResults([]);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-3/4 max-w-4xl">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold dark:text-white">Buscar Beneficiário</h3>
                    <button onClick={handleClose} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
                        <FaTimes />
                    </button>
                </div>
                <div className="mb-4">
                    <TextEdit
                        label="CPF/CNPJ ou Nome"
                        value={searchValue}
                        onChange={(e) => handleSearch(e.target.value)}
                        placeholder=""
                    />
                </div>
                <div className="overflow-x-auto h-80">
                    <table className="w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider border-b dark:border-gray-600 h-12 w-auto">Documento</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider border-b dark:border-gray-600 h-12 w-full">Nome</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider border-b dark:border-gray-600 h-12 w-auto">Ação</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-600">
                            {isSearching ? (
                                <tr>
                                    <td colSpan={3} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                                        Buscando...
                                    </td>
                                </tr>
                            ) : (
                                searchResults.slice(0, 6).map((result, index) => (
                                    <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700 h-12">
                                        <td className="px-4 py-3 whitespace-nowrap text-sm dark:text-gray-300 w-auto">
                                            {result.label.split(' - ')[0]}
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap text-sm dark:text-gray-300 w-full truncate">
                                            {result.label.split(' - ')[1]}
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap text-sm w-auto">
                                            <button
                                                onClick={() => handleSelect(result)}
                                                className="bg-blue-500 text-white px-3 py-1 rounded text-xs hover:bg-blue-600"
                                            >
                                                Selecionar
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default PersonSearchModal;
